import {
  GOVERNING_LAW,
  LEGAL_ADDRESS,
  LEGAL_NAME,
  LEGAL_PHONE,
  PRIVACY_EMAIL,
  type LegalSection,
} from './business';

export const PRIVACY_INTRO =
  `${LEGAL_NAME} ("we", "us", or "our") respects your privacy. This Privacy Policy explains how we collect, use, disclose, store, and protect personal data when you use our website, customer account, booking system, payments, wallet, and salon services. This Policy is designed to align with the Nigeria Data Protection Regulation (NDPR) and other applicable laws in the ${GOVERNING_LAW}.`;

export const PRIVACY_ADDITIONAL_NOTICE = [
  'By using our Services, you acknowledge that you have read this Privacy Policy. If you do not agree, please do not use the Services.',
  'This Policy should be read together with our Terms of Use, which describe how we apply penalties, wallet debits, and account actions that may involve your personal data.',
  'This document is provided for operational transparency and does not constitute legal advice. Consult qualified counsel for compliance specific to your situation.',
];

export const privacySections: LegalSection[] = [
  {
    title: 'Who We Are',
    content: [
      `${LEGAL_NAME} is the data controller responsible for personal data described in this Policy.`,
      `Privacy inquiries and data subject requests: ${PRIVACY_EMAIL}`,
      LEGAL_ADDRESS,
      `Phone: ${LEGAL_PHONE}`,
    ],
  },
  {
    title: 'Scope',
    content: [
      'This Policy applies to personal data processed when you:',
      '• Visit our website or use our customer portal',
      '• Create or manage an account',
      '• Book, attend, or cancel appointments',
      '• Make payments or use your wallet',
      '• Request withdrawals',
      '• Receive emails, SMS, push, or in-app notifications',
      '• Interact with our staff at the salon',
      'This Policy does not apply to third-party websites or services linked from our platform unless we expressly state otherwise.',
    ],
  },
  {
    title: 'Information We Collect',
    content: [
      'We collect information you provide directly, including:',
      '• Identity and contact: name, email, phone number, date of birth, gender, profile photo, and location details you submit',
      '• Account and authentication: one-time login codes, sign-in provider identifiers (Google/Apple), passkey data where used, and email verification status',
      '• Appointment data: selected services, date and time, notes, home-service flag, assigned stylist, cancellation reasons, and appointment images you or staff upload',
      '• Payment and wallet: transaction amounts, Paystack references, payment method, wallet balance history, penalties, levies, and idempotency records',
      '• Withdrawal banking: bank name, account number, and account holder name when you request a payout',
      '• Hair service records: hair attributes (texture, porosity, density, curl pattern, color, length, scalp notes) created by staff for service quality',
      'We also collect information automatically, including:',
      '• Device and push: mobile device tokens for notifications',
      '• Technical: IP address, browser type, user agent, activity logs, and cookies or similar technologies',
      '• Communications metadata: delivery status of emails and messages we send',
    ],
  },
  {
    title: 'How We Use Your Information',
    content: [
      'We use personal data to:',
      '• Provide, schedule, and manage salon services and appointments',
      '• Process payments, wallet credits/debits, penalties, refunds, and withdrawals',
      '• Authenticate users and secure accounts',
      '• Send transactional notifications (confirmations, reminders, cancellations, payment receipts)',
      '• Maintain hair records and service history for quality and continuity of care',
      '• Prevent fraud, enforce our Terms, and protect our staff and customers',
      '• Comply with legal, tax, and regulatory obligations',
      '• Improve our website, services, and customer experience through analytics where enabled',
    ],
  },
  {
    title: 'Legal Bases for Processing (NDPR)',
    content: [
      'Depending on the activity, we rely on one or more of the following lawful bases:',
      '• Performance of a contract: to deliver booked services, process payments, and operate your account',
      '• Legitimate interests: security, fraud prevention, debt and penalty recovery, internal reporting, and service improvement, balanced against your rights',
      '• Consent: where required for optional marketing or certain communications; you may withdraw consent without affecting lawful processing before withdrawal',
      '• Legal obligation: where we must retain or disclose data to comply with law',
    ],
  },
  {
    title: 'Sharing and Processors',
    content: [
      'We do not sell your personal data.',
      'We may share data with:',
      '• Payment processors (e.g. Paystack) to process card transactions',
      '• Authentication and push providers (e.g. Google Firebase) for sign-in and notifications',
      '• Email and SMS providers to deliver transactional messages',
      '• Hosting and infrastructure providers that store and process data on our behalf',
      '• Authorized staff and contractors with role-based access who need the data to perform their duties',
      'We may also disclose information when required by law, court order, or government request, or to protect rights, safety, and property of ' +
        LEGAL_NAME +
        ', our customers, and others.',
      'All processors are required to handle data consistent with this Policy and applicable law.',
    ],
  },
  {
    title: 'International Transfers',
    content: [
      'Some service providers may process data outside Nigeria. Where this occurs, we implement appropriate safeguards such as contractual protections and security measures consistent with NDPR requirements.',
    ],
  },
  {
    title: 'Data Retention',
    content: [
      'We retain personal data for as long as your account is active and as needed to provide Services, resolve disputes, enforce agreements, and meet legal, tax, and accounting requirements.',
      'Appointment, payment, and wallet records may be retained for several years as required for audit and compliance.',
      'When you request deletion, we will remove or anonymize data where possible, subject to legal holds and legitimate business needs (e.g. fraud prevention, outstanding balances).',
    ],
  },
  {
    title: 'Data Security',
    content: [
      'We implement technical and organizational measures designed to protect personal data, including encrypted transmission (HTTPS), access controls, authentication safeguards, and staff training.',
      'No method of transmission or storage is completely secure. You are responsible for safeguarding your account credentials and devices.',
    ],
  },
  {
    title: 'Your Rights',
    content: [
      'Under the NDPR and applicable law, you may have the right to:',
      '• Request access to personal data we hold about you',
      '• Request correction of inaccurate or incomplete data',
      '• Request deletion or restriction of processing, subject to legal exceptions',
      '• Object to certain processing based on legitimate interests',
      '• Withdraw consent where processing is based on consent',
      '• Lodge a complaint with the Nigeria Data Protection Commission (NDPC)',
      `To exercise your rights, email ${PRIVACY_EMAIL} with sufficient information to verify your identity. We will respond within timeframes required by law.`,
    ],
  },
  {
    title: 'Wallet, Penalties, and Financial Data',
    content: [
      'We process payment and wallet data to complete transactions, apply penalties or levies per our Terms, maintain transaction history, and process withdrawal requests.',
      'Penalty debits may result in negative wallet balances as disclosed in our Terms. Bank details provided for withdrawals are used solely to fulfill approved payout requests and are not used for marketing.',
    ],
  },
  {
    title: 'Hair Records and Photos',
    content: [
      'Hair profile and appointment photos are collected to deliver personalized services and maintain service history. They are accessed by authorized staff only.',
      'You may request correction of inaccurate records. Deletion requests will be honored where possible unless retention is required for legal, dispute, or quality-assurance purposes.',
    ],
  },
  {
    title: 'Cookies and Similar Technologies',
    content: [
      'Our website may use cookies, local storage, and similar technologies to:',
      '• Enable core functionality (sessions, preferences, authentication state)',
      '• Measure performance and usage to improve the Services',
      '• Remember your settings between visits',
      'You can control cookies through your browser settings. Disabling certain cookies may limit platform functionality.',
    ],
  },
  {
    title: "Children's Privacy",
    content: [
      'Our Services are not directed at children under 18 to create accounts independently. Parents or guardians may book services for minors and must provide consent for processing the minor\'s personal data where required.',
      'If you believe we have collected data from a child without appropriate consent, contact us at ' +
        PRIVACY_EMAIL +
        ' and we will take appropriate steps.',
    ],
  },
  {
    title: 'Marketing Communications',
    content: [
      'We may send promotional messages about offers and services where permitted by law and your preferences. Transactional messages (appointments, payments, security alerts) are not marketing and may be sent as necessary to provide the Services.',
      'You may opt out of marketing emails by following unsubscribe instructions or contacting us, without affecting essential service communications.',
    ],
  },
  {
    title: 'Changes to This Policy',
    content: [
      'We may update this Privacy Policy from time to time. The "Last Updated" date at the top of this page will reflect the latest version.',
      'Material changes may be communicated via the website, in-app notice, or email where appropriate. Continued use after changes constitutes acknowledgment of the updated Policy.',
    ],
  },
  {
    title: 'Contact Us',
    content: [
      'For privacy questions, data subject requests, or concerns about our practices:',
      LEGAL_NAME,
      LEGAL_ADDRESS,
      `Phone: ${LEGAL_PHONE}`,
      `Email: ${PRIVACY_EMAIL}`,
      'You may also contact the Nigeria Data Protection Commission (NDPC) if you believe your data protection rights have been violated.',
    ],
  },
];
